import { Vector3 } from 'three';

import { isDefined } from '../../../../../utils/variables';
import { AttributeData, AttributeId } from '../../skymap-extras';
import { DesignNode, RevertChange } from '../design-node';
import {
  Conic,
  getConicCenter,
  getConicLength,
  getConicPoint,
  getSegmentConic,
  getSegmentCurve,
} from './conic';
import { LineSegmentId, recalculateLineSegmentMetaData } from './line-segment-node';
import { PointId, PointNode } from './point-node';

/**
 * A circle or ellipse of a design: a closed loop of curved line segments around a center point,
 * joined by seams, points that only join two of its arcs (see skymap-oda's
 * shared/cpp/glb/README.md, Seams). The seams are not shown, and the loop acts as one while it is
 * intact: when anything else joins it, it is arcs and points like any other.
 */
export type DesignCircle = {
  center: PointId;
  /** Along the loop. */
  seams: PointId[];
  /** Along the loop, from the first seam. */
  lineSegments: LineSegmentId[];
};

/**
 * Walks the closed loop of curved line segments from the point, through the points that join it.
 * Returns undefined if the loop is not closed, or a join has anything else connected to it.
 */
function walkLoop(
  node: DesignNode,
  start: PointId,
  isJoin: (point: PointNode) => boolean,
): { points: PointId[]; lineSegments: LineSegmentId[] } | undefined {
  const isLoopJoin = (point: PointNode) =>
    isJoin(point) && point.segments.length === 2 && point.faces.length === 0;

  const first = node.getPointById(start);

  if (!isLoopJoin(first)) {
    return undefined;
  }

  const points = [start];
  const lineSegments: LineSegmentId[] = [];
  let point = first;
  let segmentId = first.segments[0];

  for (;;) {
    const segment = node.getLineSegmentById(segmentId);

    if (!segment.curve) {
      return undefined;
    }

    lineSegments.push(segmentId);
    const nextId = segment.values[0] === point.id ? segment.values[1] : segment.values[0];

    if (nextId === start) {
      return { points, lineSegments };
    }

    const next = node.getPointById(nextId);

    if (!isLoopJoin(next) || points.includes(nextId)) {
      return undefined;
    }

    points.push(nextId);
    point = next;
    segmentId = next.segments[0] === segmentId ? next.segments[1] : next.segments[0];
  }
}

/**
 * Gets the circle that the point is a seam of, if it is intact: the line segments from the point
 * are curved and close a loop, joined only by seams of the same center point.
 */
export function getSeamCircle(node: DesignNode, id: PointId): DesignCircle | undefined {
  const center = node.getPointById(id).seamOf;

  if (!isDefined(center) || !node.hasPoint(center)) {
    return undefined;
  }

  const loop = walkLoop(node, id, (point) => point.seamOf === center);
  return loop && { center, seams: loop.points, lineSegments: loop.lineSegments };
}

/**
 * Gets the intact circle that the line segment is an arc of.
 */
export function getLineSegmentCircle(
  node: DesignNode,
  id: LineSegmentId,
): DesignCircle | undefined {
  for (const pointId of node.getLineSegmentById(id).values) {
    if (isDefined(node.getPointById(pointId).seamOf)) {
      const circle = getSeamCircle(node, pointId);
      return circle?.lineSegments.includes(id) ? circle : undefined;
    }
  }

  return undefined;
}

/**
 * Whether the point is a seam of an intact circle, which is not shown or selected on its own.
 */
export function isHiddenSeam(node: DesignNode, id: PointId): boolean {
  return isDefined(node.getPointById(id).seamOf) && isDefined(getSeamCircle(node, id));
}

/**
 * Adds the other arcs of the intact circles that the line segments are arcs of, since a circle is
 * hovered and selected as one.
 */
export function withWholeCircles(node: DesignNode, ids: LineSegmentId[]): LineSegmentId[] {
  const result = new Set<LineSegmentId>();

  for (const id of ids) {
    if (result.has(id)) {
      continue;
    }

    for (const x of getLineSegmentCircle(node, id)?.lineSegments ?? [id]) {
      result.add(x);
    }
  }

  return [...result];
}

/**
 * Gets the intact circles around the center point.
 */
export function getCenterCircles(node: DesignNode, center: PointId): DesignCircle[] {
  const circles: DesignCircle[] = [];
  const visited = new Set<PointId>();

  for (const id of node.getSeams(center)) {
    if (visited.has(id)) {
      continue;
    }

    const circle = getSeamCircle(node, id);
    circle?.seams.forEach((x) => visited.add(x));

    if (circle) {
      circles.push(circle);
    }
  }

  return circles;
}

/**
 * Gets the seams of the intact circles, which are not shown.
 */
export function getHiddenSeams(node: DesignNode): Set<PointId> {
  const hidden = new Set<PointId>();
  const visited = new Set<PointId>();

  for (const point of node.pointNodes) {
    if (!isDefined(point.seamOf) || visited.has(point.id)) {
      continue;
    }

    const circle = getSeamCircle(node, point.id);
    circle?.seams.forEach((x) => {
      visited.add(x);
      hidden.add(x);
    });
  }

  return hidden;
}

/**
 * Adds a circle or ellipse around the center point: a loop of the curves, which start where the
 * one before ends, with a seam at the start of each.
 */
export function addCircle(
  node: DesignNode,
  center: PointId,
  conics: Conic[],
  attributeData?: Map<AttributeId, AttributeData>,
): RevertChange[] {
  const changes: RevertChange[] = [];
  const seams = conics.map((conic) => {
    const change = node.addPoint(conic.start.clone(), undefined, center);
    changes.unshift(change);
    return change.id;
  });

  conics.forEach((conic, i) => {
    changes.unshift(
      node.addLineSegment(
        seams[i],
        seams[(i + 1) % seams.length],
        attributeData,
        getSegmentCurve(conic),
      ),
    );
  });

  return changes;
}

/**
 * Makes circles and ellipses of the closed loops of curves that are joined only by the seams,
 * e.g. of an imported drawing, where they have no center point: a center point is added, if the
 * loop is horizontal.
 */
export function adoptSeamLoops(node: DesignNode, seams: Set<PointId>) {
  const visited = new Set<PointId>();

  for (const id of seams) {
    if (visited.has(id) || !node.hasPoint(id)) {
      continue;
    }

    const loop = walkLoop(node, id, (point) => seams.has(point.id) && !isDefined(point.seamOf));
    loop?.points.forEach((x) => visited.add(x));

    const conics = loop?.lineSegments.map((segmentId) => {
      const segment = node.getLineSegmentById(segmentId);
      const [start, end] = segment.values.map((x) => node.getPointById(x).vertex);
      return getSegmentConic(start, end, segment.curve!);
    });
    const center = conics && getConicCenter(conics[0]);

    if (!conics || !center) {
      continue;
    }

    // A circle or an ellipse in the horizontal plane, as the design tool edits them.
    const tolerance = 1e-3 * conics[0].start.distanceTo(conics[0].end);
    const isHorizontal = conics.every((conic) =>
      [conic.start, conic.control, conic.end].every((x) => Math.abs(x.z - center.z) <= tolerance),
    );
    const isAround = conics.every(
      (conic) => (getConicCenter(conic)?.distanceTo(center) ?? Infinity) <= tolerance,
    );

    if (isHorizontal && isAround) {
      const centerId = node.addPoint(center).id;
      loop!.points.forEach((x) => node.upsertPoint({ id: x, seamOf: centerId }));
    }
  }
}

/**
 * Moves the intact circles around the center point by the offset, in the coordinates of the
 * points. The curves keep their shape, since they are relative to their ends.
 */
export function moveCircles(node: DesignNode, center: PointId, offset: Vector3): RevertChange[] {
  return getCenterCircles(node, center)
    .flatMap((circle) => circle.seams)
    .map((id) =>
      node.upsertPoint({ id, vertex: node.getPointById(id).vertex.clone().add(offset) }),
    );
}

/**
 * Gets the quarter arcs of the circle or ellipse through the ends of its axes, at the offsets from
 * the center, along the loop: each from one end to the next, with its control point at the corner
 * of the parallelogram around it, see skymap-oda's shared/cpp/glb/README.md (Seams).
 */
export function getQuarterConics(center: Vector3, offsets: Vector3[]): Conic[] {
  return offsets.map((offset, i) => {
    const next = offsets[(i + 1) % offsets.length];
    return {
      start: center.clone().add(offset),
      control: center.clone().add(offset).add(next),
      end: center.clone().add(next),
      weight: Math.SQRT1_2,
    };
  });
}

/**
 * Gets the handles of a circle or ellipse in world coordinates: the seams at the ends of its axes,
 * and the middles of its arcs, along the loop.
 */
export function getCircleHandles(node: DesignNode, circle: DesignCircle) {
  return {
    corners: circle.seams.map((id) => node.getVertexByPointId(id, 'world')),
    middles: circle.lineSegments.map((id) => {
      const segment = node.getLineSegmentById(id);
      const [start, end] = node.getVerticesByLineSegmentId(id, 'world');
      return getConicPoint(getSegmentConic(start, end, segment.curve!), 0.5);
    }),
  };
}

/**
 * Gets the offsets of the seams from the center, in the coordinates of the points.
 */
export function getSeamOffsets(node: DesignNode, circle: DesignCircle): Vector3[] {
  const center = node.getPointById(circle.center).vertex;
  return circle.seams.map((id) => node.getPointById(id).vertex.clone().sub(center));
}

/**
 * Reshapes a circle or ellipse: moves its seams to the offsets from the center, in the coordinates
 * of the points, and makes the arcs the quarters between them. The stripes run on along the loop.
 */
export function reshapeCircle(
  node: DesignNode,
  circle: DesignCircle,
  offsets: Vector3[],
): RevertChange[] {
  const center = node.getPointById(circle.center).vertex;
  const changes: RevertChange[] = circle.seams.map((id, i) =>
    node.upsertPoint({ id, vertex: center.clone().add(offsets[i]) }),
  );

  let stripeOffset = node.getLineSegmentById(circle.lineSegments[0]).stripe?.offset ?? 0;

  circle.lineSegments.forEach((id) => {
    const segment = node.getLineSegmentById(id);
    const [start, end] = segment.values.map((x) => node.getPointById(x).vertex);
    const conic = {
      start,
      control: start.clone().add(end).sub(center),
      end,
      weight: Math.SQRT1_2,
    };

    changes.unshift(
      node.upsertLineSegment({
        id,
        curve: getSegmentCurve(conic),
        stripe: segment.stripe && { ...segment.stripe, offset: stripeOffset },
      }),
    );
    stripeOffset += getConicLength(conic);
  });

  recalculateLineSegmentMetaData(node, circle.lineSegments);
  return changes;
}

/**
 * Breaks a circle or ellipse into arcs and points like any other: its seams are shown.
 */
export function breakCircle(node: DesignNode, circle: DesignCircle): RevertChange[] {
  return circle.seams.map((id) => node.upsertPoint({ id, seamOf: undefined }));
}
